<template>
  <div class="w-full h-chart flex justify-center relative">
    <div class="w-chart h-full" ref="charts"></div>
    <div
      v-if="source.length === 0"
      class="w-full h-full absolute left-0 top-0 z-10 flex flex-col items-center justify-center bg-white"
    >
      <el-empty :image-size="260" description="暂无图例" />
    </div>
  </div>
</template>

<script>
import echarts from 'echarts/lib/echarts'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/dataZoom'
import 'echarts/lib/chart/radar'
import 'echarts/lib/chart/bar'
import 'echarts/lib/chart/line'
import { chartTypeCode } from './chartType'
import { busGetters } from 'dygc'

export default {
  name: 'AnalysisChart',
  props: {
    type: {
      type: String,
      default: ''
    },
    source: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      LEGEND: ['标准分值', '得分分值'],
      COLOR: ['#2775E8', '#00BE6A'],
      chart: null,
      ITEM_SIZE: 120,
      grid: {
        left: '20%',
        right: '5%'
      }
    }
  },
  watch: {
    type() {
      this.setChart()
    },
    source: {
      handler() {
        this.setChart()
      },
      deep: true
    }
  },
  mounted() {
    this.initChart()
    this.setChart()
  },
  beforeDestroy() {
    this.destroyChart()
  },
  computed: {
    ...busGetters(['openStandardScore'])
  },
  methods: {
    initChart() {
      if (this.$refs.charts) {
        this.chart = echarts.init(this.$refs.charts)
      }
    },
    destroyChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },
    getSeries({ scoreList, standardList }) {
      let list = []
      if (this.openStandardScore) {
        list = [standardList, scoreList]
      } else {
        this.LEGEND = ['得分分值']
        this.COLOR = ['#00BE6A']
        list = [scoreList]
      }
      const radar = [
        {
          name: '数据',
          type: 'radar',
          data: []
        }
      ]
      const bar = []
      const line = []
      list.forEach((arr, i) => {
        const hexColor = this.COLOR[i].replace('#', '')
        const R = `${hexColor[0]}${hexColor[1]}`
        const G = `${hexColor[2]}${hexColor[3]}`
        const B = `${hexColor[4]}${hexColor[5]}`
        const areaColor = `rgba(${parseInt(R, 16)},${parseInt(G, 16)},${parseInt(B, 16)}, 0.5)`
        radar[0].data.push({
          name: this.LEGEND[i],
          value: arr,
          areaStyle: {
            color: areaColor
          }
        })
        bar.push({
          type: 'bar',
          barMaxWidth: 50,
          name: this.LEGEND[i],
          data: arr
        })
        line.push({
          type: 'line',
          name: this.LEGEND[i],
          data: arr
        })
      })
      return { radar, bar, line }
    },
    getSource() {
      const maxList = []
      const indicatorList = []
      const scoreList = []
      const standardList = []
      for (const se of this.source) {
        const ms = Math.max(se.score, se.standardScore)
        maxList.push(ms)
        scoreList.push(se.score)
        standardList.push(se.standardScore)
      }
      const maxScore = maxList.length > 0 ? Math.max(...maxList) : 0
      const max = Math.ceil(maxScore / 5) * 5 === 0 ? 1 : Math.ceil(maxScore / 5) * 5
      for (const se of this.source) {
        const { title = '' } = se
        indicatorList.push({ name: title, max })
      }
      if (indicatorList[0]) {
        indicatorList[0].axisLabel = { show: true }
      }
      return { indicatorList, scoreList, standardList }
    },
    getOptions() {
      const { indicatorList, scoreList, standardList } = this.getSource()
      const { radar, bar, line } = this.getSeries({ scoreList, standardList })
      const titleList = indicatorList.map(m => m.name)
      const dataZoom = this.getSliderSetting();
      const sliderSetting = dataZoom === null ? {} : { dataZoom };
      switch (this.type) {
        case chartTypeCode.LEIDA:
          return {
            color: this.COLOR,
            tooltip: {
              show: true,
              formatter:function (params){
                let names = titleList;
                let data = '';
                for(let i=0; i<params.data.value.length; i++){
                  data += names[i] + ': '+ params.data.value[i]+ '<br/>';
                }
                return params.data.name+'<br/>'+data;
              }
            },
            legend: {
              x: 'right',
              data: this.LEGEND
            },
            radar: {
              indicator: indicatorList,
              radius: 120,//缩放
              center: ['50%', '55%'],//位置
              name:{
                formatter: this.axisLabelFormatter
              },
              splitArea: {
                areaStyle: {
                  color: new Array(5).fill('#fff')
                }
              }
            },
            series: radar
          }
        case chartTypeCode.ZHUZHUANG:
          return {
            color: this.COLOR,
            tooltip: { show: true },
            grid: {
              left: '20%',
              right: '5%',
              bottom: dataZoom === null ? 15 : 85
            },
            legend: {
              x: 'right',
              data: this.LEGEND
            },
            xAxis: {
              type: 'category',
              axisLabel: {
                interval: 0,
                formatter: this.axisLabelFormatter
              },
              data: titleList
            },
            yAxis: {
              type: 'value'
            },
            series: bar,
            ...sliderSetting
          }
        case chartTypeCode.ZHEXIAN:
          return {
            color: this.COLOR,
            tooltip: { show: true },
            grid: {
              left: '20%',
              right: '5%',
              bottom: dataZoom === null ? 15 : 85
            },
            legend: {
              x: 'right',
              data: this.LEGEND
            },
            xAxis: {
              type: 'category',
              axisLabel: {
                interval: 0,
                formatter: this.axisLabelFormatter
              },
              data: titleList
            },
            yAxis: {
              type: 'value'
            },
            series: line,
            ...sliderSetting
          }
        default:
          return {}
      }
    },
    axisLabelFormatter(str) {
      let result = str.slice(0,5) + '...'
      return result
    },
    getSliderSetting() {
      let end = null;
      if (this.$refs.charts) {
        const { width } = this.$refs.charts.getBoundingClientRect();
        const scale = width / (this.ITEM_SIZE * this.source.length);
        if (scale < 1) {
          end = Math.round(scale * 100);
        }
      }
      if (end === null) return null;
      return {
        type: "slider",
        showDetail: false,
        borderColor: "transparent",
        backgroundColor: "rgba(244,244,244, 0.7)",
        fillerColor: "#d8d8d8",
        dataBackground: {
          lineStyle: {
            color: "transparent",
            with: 0
          },
          areaStyle: {
            color: "transparent",
            with: 0
          }
        },
        filterMode: "empty",
        handleSize: 0,
        height: 12,
        bottom: 0,
        start: 0,
        end
      }
    },
    setChart() {
      if (this.chart) {
        const option = this.getOptions()
        this.chart.clear()
        if (this.source.length > 0) {
          this.chart.setOption(option)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.w-chart {
  width: 100%;
}
.h-chart {
  height: 385px;
}
</style>
